import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, UserPlus, X } from 'lucide-react';
import Confirm from './ui/Confirm';
import {
  createGuardian,
  linkStudent,
  linksOfStudent,
  listGuardians,
  revokeLink,
  siblingsOfStudent,
  type Guardian,
  type GuardianLink,
} from '../services/guardians';

// Bloque de familia de la ficha del alumno (F4). Convive con el bloque legacy
// "Datos del tutor" (las tres columnas de texto de `profiles`): mientras haya
// alumnos sin vínculo, ese sigue siendo el único dato que existe para ellos. En
// cuanto hay vínculo, la fuente de verdad es ésta.
//
// ============================================================
// LAS DOS ACCIONES PESAN LO MISMO
// ============================================================
// Hasta la serie de Familias, retirar un vínculo preguntaba y CONCEDERLO no:
// se pulsaba "Vincular" y un adulto pasaba a ver la ficha entera de un menor
// —datos médicos incluidos— sin un solo paso intermedio. Estaba al revés.
// Conceder es el acto que crea el acceso; retirarlo es el que lo cierra.
//
// Las dos confirmaciones nombran a las DOS personas. No es cortesía: es lo
// único que separa "vincular al tutor de este alumno" de "vincular al tutor
// equivocado" cuando las dos filas de la tabla se ven idénticas.
//
// El rastro va aparte, en `audit_log`, y lo escribe un trigger sobre
// `guardian_students` (mig 210) — no esta pantalla. Aquí hay un camino de los
// cinco que escriben esa tabla.

const RELATIONSHIPS = [
  { value: 'madre', label: 'Madre' },
  { value: 'padre', label: 'Padre' },
  { value: 'tutor_legal', label: 'Tutor legal' },
  { value: 'otro', label: 'Otro' },
];

type LinkRow = GuardianLink & { guardian: Guardian | null };

export default function FamilyBlock({
  studentId,
  studentName,
}: {
  studentId: string;
  /** Para nombrar al alumno en las confirmaciones. */
  studentName: string;
}) {
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [siblings, setSiblings] = useState<{ id: string; full_name: string }[]>([]);
  const [guardians, setGuardians] = useState<Guardian[]>([]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  // «Sin tutor vinculado» es una afirmación sobre quién puede ver los datos de
  // un menor. Y `links` decide además el `is_primary` del vínculo nuevo (ver
  // `doLink`), así que una lectura fallida no solo se pintaba mal: se escribía.
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmLink, setConfirmLink] = useState<{ name: string; isNew: boolean } | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<LinkRow | null>(null);

  const [pickedGuardian, setPickedGuardian] = useState('');
  const [relationship, setRelationship] = useState('madre');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');

  const load = useCallback(async () => {
    setErrorCarga(null);
    const [l, s] = await Promise.all([linksOfStudent(studentId), siblingsOfStudent(studentId)]);
    if (l.error || s.error) {
      setErrorCarga(l.error ?? s.error);
      return;
    }
    setLinks(l.vinculos);
    setSiblings(s.hermanos);
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  async function openAdd() {
    setAdding(true);
    setError('');
    const { tutores, error: errorTutores } = await listGuardians();
    // Sin el censo de tutores el desplegable sale vacío y parece que no hay
    // ninguno: quien vaya a vincular crearía un duplicado del que ya existe.
    if (errorTutores) {
      setError(`No se ha podido cargar la lista de tutores: ${errorTutores}`);
      return;
    }
    setGuardians(tutores);
  }

  // Pulsar "Vincular" ya NO vincula: valida y pregunta. La creación del tutor
  // nuevo se deja para después de la confirmación — si preguntara con la ficha
  // ya creada, cancelar dejaría un tutor huérfano en la tabla.
  function askLink() {
    setError('');
    if (!pickedGuardian && !newName.trim()) {
      setError('Pon el nombre del tutor.');
      return;
    }
    const name = pickedGuardian
      ? (guardians.find((g) => g.id === pickedGuardian)?.full_name ?? 'este tutor')
      : newName.trim();
    setConfirmLink({ name, isNew: !pickedGuardian });
  }

  async function doLink() {
    // El objetivo se captura ANTES del primer await: `Confirm` cierra el
    // diálogo justo tras invocar onConfirm (patrón de DirectoresPage).
    const target = confirmLink;
    if (!target) return;
    setBusy(true);
    setError('');
    let guardianId = pickedGuardian;

    // Tutor nuevo: se crea la ficha y se vincula en el mismo gesto. Crearle
    // CUENTA es otra cosa y va por Edge Function, como monitores y directores.
    if (!guardianId) {
      const created = await createGuardian(newName, newEmail || null, newPhone || null);
      if (!created.ok || !created.id) {
        setError(created.message ?? 'No se pudo crear el tutor.');
        setBusy(false);
        return;
      }
      guardianId = created.id;
    }

    // ⚠️ El cuarto argumento es `is_primary`, y sale de `links.length === 0`.
    // Con la lectura de vínculos caída eso valía `true` SIEMPRE, así que un
    // fallo de red marcaba al tutor nuevo como principal por encima del que ya
    // lo era. Por eso `load` no pinta la lista si no pudo leerla, y aquí no se
    // llega: el bloque entero se sustituye por el aviso.
    const res = await linkStudent(guardianId, studentId, relationship, links.length === 0);
    setBusy(false);
    if (!res.ok) {
      setError(res.message ?? 'No se pudo vincular.');
      return;
    }
    setAdding(false);
    setPickedGuardian('');
    setNewName('');
    setNewEmail('');
    setNewPhone('');
    await load();
  }

  async function doRevoke() {
    const target = confirmRevoke;
    if (!target) return;
    setBusy(true);
    const res = await revokeLink(target.id);
    setBusy(false);
    if (!res.ok) {
      setError(res.message ?? 'No se pudo retirar.');
      return;
    }
    await load();
  }

  return (
    <div className="border-t border-bg-border pt-4 space-y-3" data-testid="family-block">
      <div className="flex items-center justify-between">
        <h4 className="text-text-secondary text-xs uppercase tracking-wider flex items-center gap-2">
          <Users size={14} /> Familia
        </h4>
        {/* Vincular decide el `is_primary` a partir de `links.length`, así que
            sin haber podido leer los vínculos el botón no se ofrece: no es que
            quede feo, es que escribiría un dato decidido a ciegas. */}
        {!adding && !errorCarga && (
          <button className="btn-ghost text-xs" onClick={openAdd} data-testid="add-guardian">
            <UserPlus size={14} /> Vincular tutor
          </button>
        )}
      </div>

      {error && <p className="text-error text-xs">{error}</p>}

      {/* No se pudo leer quién tiene acceso. Ni «sin tutor» ni la lista: se
          cuenta y se deja reintentar. Vincular desde aquí decidiría además el
          tutor principal sobre un recuento que nadie leyó. */}
      {errorCarga && (
        <div className="text-error text-xs space-y-2" data-testid="family-block-error">
          <p>No se ha podido cargar la familia de este alumno. {errorCarga}</p>
          <button
            className="btn-ghost text-xs"
            onClick={load}
            data-testid="family-block-reintentar"
          >
            Reintentar
          </button>
        </div>
      )}

      {!errorCarga && links.length === 0 && !adding && (
        <p className="text-text-secondary text-xs" data-testid="no-guardians">
          Sin tutor vinculado.
        </p>
      )}

      {links.map((l) => (
        <div
          key={l.id}
          className="flex items-center justify-between bg-bg-elevated rounded px-3 py-2"
          data-testid={`guardian-${l.id}`}
        >
          <div>
            <p className="text-sm font-medium">{l.guardian?.full_name ?? '—'}</p>
            <p className="text-text-secondary text-xs">
              {RELATIONSHIPS.find((r) => r.value === l.relationship)?.label ?? 'Tutor'}
              {l.guardian?.email ? ` · ${l.guardian.email}` : ''}
              {/* Sin cuenta = tutor declarativo: no puede entrar en la app todavía. */}
              {l.guardian?.profile_id ? '' : ' · sin cuenta'}
            </p>
          </div>
          <button
            className="btn-ghost text-xs text-error"
            onClick={() => setConfirmRevoke(l)}
            disabled={busy}
            data-testid={`revoke-${l.id}`}
          >
            <X size={14} /> Retirar
          </button>
        </div>
      ))}

      {siblings.length > 0 && (
        <div data-testid="siblings">
          <p className="text-text-secondary text-xs uppercase tracking-wider mb-1">
            {siblings.length === 1 ? 'Hermano' : 'Hermanos'}
          </p>
          <div className="flex flex-wrap gap-2">
            {siblings.map((s) => (
              <Link
                key={s.id}
                to={`/alumnos/${s.id}`}
                className="badge-info text-xs hover:underline"
                data-testid={`sibling-${s.id}`}
              >
                {s.full_name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {adding && (
        <div className="bg-bg-elevated rounded p-3 space-y-3" data-testid="add-guardian-form">
          <select
            className="input w-full text-sm"
            value={pickedGuardian}
            onChange={(e) => setPickedGuardian(e.target.value)}
            data-testid="guardian-picker"
          >
            <option value="">— Tutor nuevo —</option>
            {guardians.map((g) => (
              <option key={g.id} value={g.id}>
                {g.full_name}
                {g.email ? ` (${g.email})` : ''}
              </option>
            ))}
          </select>

          {/* Elegir uno EXISTENTE es lo que hace que dos hermanos compartan
              tutor, y con él un solo email y una sola firma. */}
          {!pickedGuardian && (
            <div className="grid grid-cols-3 gap-2">
              <input
                className="input text-sm"
                placeholder="Nombre y apellidos"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                data-testid="guardian-name"
              />
              <input
                className="input text-sm"
                placeholder="Email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                data-testid="guardian-email"
              />
              <input
                className="input text-sm"
                placeholder="Teléfono"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                data-testid="guardian-phone"
              />
            </div>
          )}

          <select
            className="input w-full text-sm"
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            data-testid="guardian-relationship"
          >
            {RELATIONSHIPS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>

          <div className="flex gap-2">
            <button
              className="btn-primary text-sm"
              onClick={askLink}
              disabled={busy}
              data-testid="confirm-link"
            >
              Vincular
            </button>
            <button className="btn-ghost text-sm" onClick={() => setAdding(false)} disabled={busy}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Conceder acceso. `danger` está puesto a propósito aunque no se borre
          nada: lo que se hace aquí es abrir la ficha completa de un menor. */}
      <Confirm
        open={confirmLink !== null}
        onClose={() => setConfirmLink(null)}
        onConfirm={doLink}
        title="Dar acceso a los datos del alumno"
        confirmLabel="Dar acceso"
        danger
        message={
          confirmLink
            ? `${confirmLink.name} podrá ver la ficha completa de ${studentName}, incluidos sus datos médicos, su calendario y su conversación con el entrenador, y podrá apuntarle y desapuntarle de las sesiones.` +
              (confirmLink.isNew ? ' Se creará su ficha de tutor.' : '')
            : ''
        }
      />

      <Confirm
        open={confirmRevoke !== null}
        onClose={() => setConfirmRevoke(null)}
        onConfirm={doRevoke}
        title="Retirar el acceso"
        confirmLabel="Retirar"
        danger
        message={
          confirmRevoke
            ? `${confirmRevoke.guardian?.full_name ?? 'Este tutor'} dejará de ver los datos de ${studentName}. El vínculo no se borra: queda registrado cuándo terminó.`
            : ''
        }
      />
    </div>
  );
}
